import { useState, useRef, useEffect } from "react";
import { AddMemberModal } from "./models/addMemberModal";
import Cookies from "js-cookie";
import toast from "react-hot-toast";
import { appStorage } from "../../utils/appStorage";

import { useNavigate, useRoutes } from "react-router-dom";

function EditGroupModal({ open, onClose, groupData, refreshGroupDetails }) {
  const [groupName, setGroupName] = useState(groupData?.group_name || "");
  const [groupImage, setGroupImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(
    groupData?.group_image_url ? `${process.env.REACT_APP_API_BASE_URL}${groupData.group_image_url}` : null
  );
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef(null);
  const token = Cookies.get("auth_token");
  const modalRef = useRef();

  useEffect(() => {
    setGroupName(groupData?.group_name || "");
    setImagePreview(
      groupData?.group_image_url ? `${process.env.REACT_APP_API_BASE_URL}${groupData.group_image_url}` : null
    );
  }, [groupData, open]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    }
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, onClose]);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setGroupImage(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target?.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!groupName.trim()) {
      toast.error("Group name cannot be empty");
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("group_id", groupData.group_id);
      formData.append("group_name", groupName.trim());
      if (groupImage) {
        formData.append("image", groupImage);
      }

      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/group/edit`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await res.json();
      if (data?.status === "1") {
        toast.success("Group updated successfully!");

        // Refresh group details with latest data
        const groupId = data.data[0]?.group?.group_id;
        if (groupId && refreshGroupDetails) {
          await refreshGroupDetails(groupId);
        }

        onClose();
      } else {
        toast.error(data?.message || "Failed to update group");
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 backdrop-blur-sm flex items-center justify-center z-50">
      <div
        ref={modalRef}
        className="bg-white rounded-[40px] shadow-xl px-8 pt-7 pb-7 flex flex-col items-center w-[400px] max-w-[90%]"
      >
        <h3 className="text-lg font-bold text-[#000000] mb-6">Edit Group</h3>

        {/* Image Preview & Upload */}
        <div className="mb-6 flex flex-col items-center">
          <div
            className="h-24 w-24 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center cursor-pointer hover:bg-gray-300 transition mb-3"
            onClick={() => fileInputRef.current?.click()}
          >
            {imagePreview ? (
              <img src={imagePreview} alt="Group" className="h-full w-full object-cover" />
            ) : (
              <svg className="w-12 h-12 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="hidden"
          />
          <p className="text-xs text-gray-500">Click to change image</p>
        </div>

        {/* Group Name Input */}
        <div className="w-full mb-6">
          <label className="block text-sm font-semibold text-gray-900 mb-2">Group Name</label>
          <input
            type="text"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="Enter group name"
            className="w-full px-4 py-3 border border-gray-300 rounded-[20px] focus:outline-none focus:ring-2 focus:ring-[#7C4DFF] focus:border-transparent"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 w-full">
          <button
            type="button"
            onClick={handleSave}
            disabled={isLoading}
            className="submit-button w-full disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? "Saving..." : "Save Changes"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="border border-[#7A5AF8] text-[#7A5AF8] px-[30px] rounded-[25px] py-3 w-full"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function RemoveModal({ open, onClose, onDelete, member }) {

  const modalRef = useRef();

  useEffect(() => {
    function handleClickOutside(event) {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    }
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 backdrop-blur-sm flex items-center justify-center z-50">
      <div
        ref={modalRef}
        className="bg-white rounded-[40px] shadow-xl px-8 pt-7 pb-4 flex flex-col items-center w-[320px]"
      >
        <div className="text-lg font-bold text-[#000000] mb-2">
          Remove "{member.name}" from the group?
        </div>
        <div className="flex flex-col gap-4 mt-2">
          <button
            type="button"
            onClick={onDelete}
            className="submit-button w-40"
          >
            Yes
          </button>
          <button
            type="button"
            onClick={onClose}
            className="border border-[#7A5AF8] text-[#7A5AF8] px-[30px] rounded-[25px] py-3"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}


function DeleteModal({ open, onClose, onDelete }) {

  const modalRef = useRef();

  useEffect(() => {
    function handleClickOutside(event) {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    }
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 backdrop-blur-sm flex items-center justify-center z-50">
      <div
        ref={modalRef}
        className="bg-white rounded-[40px] shadow-xl px-8 pt-7 pb-4 flex flex-col items-center w-[320px]"
      >
        <div className="text-lg font-bold text-[#000000] mb-2">
          Delete this group?
        </div>
        <div className="flex flex-col gap-4 mt-2">
          <button
            type="button"
            onClick={onDelete}
            className="submit-button w-40"
          >
            Yes
          </button>
          <button
            type="button"
            onClick={onClose}
            className="border border-[#7A5AF8] text-[#7A5AF8] px-[30px] rounded-[25px] py-3"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function ExitModal({ open, onClose, onDelete }) {

  const modalRef = useRef();

  useEffect(() => {
    function handleClickOutside(event) {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    }
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 backdrop-blur-sm flex items-center justify-center z-50">
      <div
        ref={modalRef}
        className="bg-white rounded-[40px] shadow-xl px-8 pt-7 pb-4 flex flex-col items-center w-[320px]"
      >
        <div className="text-lg font-bold text-[#000000] mb-2">
          Exit from the group?
        </div>
        <div className="flex flex-col gap-4 mt-2">
          <button
            type="button"
            onClick={onDelete}
            className="submit-button w-40"
          >
            Yes
          </button>
          <button
            type="button"
            onClick={onClose}
            className="border border-[#7A5AF8] text-[#7A5AF8] px-[30px] rounded-[25px] py-3"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export const GroupDetails = ({ groupData, fetchGroups, refreshGroupDetails, onBack, handleGroupDetails }) => {
  const [showExitModal, setShowExitModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const navigate = useNavigate();

  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [removeMember, setRemoveMember] = useState(null);
  const handleExitClick = () => setShowExitModal(true);
  const handleDeleteClick = () => setShowDeleteModal(true);


  // Get current user ID from appStorage and check if admin
  const currentUserId = appStorage.getItem("user_id");
  const isAdmin = groupData?.members?.some(
    (m) => m.user_id === currentUserId && m.UserType === "Admin"
  );
  // Search UI for members (animated inline search)
  const [showSearchInput, setShowSearchInput] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");
  const searchRef = useRef(null);

  useEffect(() => {
    if (showSearchInput) {
      // focus input when opened
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [showSearchInput]);

  // close search on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setShowSearchInput(false);
        setMemberSearch("");
      }
    };
    if (showSearchInput) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showSearchInput]);

  const token = Cookies.get("auth_token");

  const handleDeleteGroup = async () => {
    try {
      const body = new URLSearchParams();
      body.append("group_id", groupData.group_id);

      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/group/delete`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: body.toString(),
      });

      const data = await res.json();
      if (data?.status === "1") {
        toast.success(data.message || "Group deleted");
        setShowExitModal(false);
        // navigate to groups list then reload to refresh data
        navigate('/groupchat');
        setTimeout(() => window.location.reload(), 250);
      } else {
        toast.error(data.message || "Failed to delete group");
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong");
    }
  };
  const handleExitGroup = async () => {
    try {
      const body = new URLSearchParams();
      body.append("group_id", groupData.group_id);

      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/group/exit`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: body.toString(),
      });

      const data = await res.json();
      if (data?.status === "1") {
        toast.success(data.message || "Group exited");
        setShowExitModal(false);
        // navigate to groups list then reload to refresh data
        navigate('/groupchat');
        setTimeout(() => window.location.reload(), 250);
      } else {
        toast.error(data.message || "Failed to delete group");
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong");
    }
  };


  const handleRemoveMember = async () => {
    if (!removeMember) return;

    try {
      const body = new URLSearchParams();
      body.append("group_id", groupData.group_id);
      body.append("member_user_id", removeMember.user_id);
      // body.append("member_phone", removeMember.phone);

      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/group/remove_members`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: body.toString(),
        }
      );

      const data = await res.json();

      if (data.status === "1") {
        toast.success("Member removed!");
        setShowRemoveModal(false);
        fetchGroups(); // Refresh group list to update members
        try {
          // Also refresh current group's details so members list updates without a page refresh
          refreshGroupDetails && await refreshGroupDetails(groupData.group_id);
        } catch (e) {
          }
      } else {
        toast.dismiss();
        toast.error(data.message || "Failed to remove member");
      }
    } catch (err) {
      toast.dismiss();
      toast.error("Something went wrong");
    }
  };

  // derived filtered members for rendering
  const filteredMembers = (groupData?.members || []).filter((m) =>
    memberSearch ? m.name?.toLowerCase().includes(memberSearch.toLowerCase()) : true
  );

  if (!groupData) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-500">Loading group details...</div>
      </div>
    );
  }

  return (
    <>
      <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white p-5 pb-0 shadow-sm md:p-6 max-md:rounded-none max-md:border-0">
        <div className="flex shrink-0 justify-between items-center">
          <div className="flex items-center gap-3">
            {onBack && (
              <button
                onClick={onBack}
                className="md:hidden p-2 hover:bg-gray-100 rounded-full transition"
                aria-label="Go back"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <h2 className="text-[20px] font-semibold text-black">Group Details</h2>
          </div>
        </div>

        <div className="flex shrink-0 max-[1150px]:flex-col max-[1150px]:items-center items-start gap-4 md:gap-10 p-4 rounded-2xl">
          <img
            src={
              groupData?.group_image_url
                ? `${process.env.REACT_APP_API_BASE_URL}${groupData.group_image_url}`
                : "./groupscreen/dummy-group.svg"
            }
            alt={groupData?.group_name || "Group"}
            className="h-16 w-16 md:h-24 md:w-24 rounded-full object-cover flex-shrink-0"
          />

          <div className="text-sm min-w-0 space-y-1 flex-1">
            <p className="font-semibold text-gray-900 text-base md:text-md aoboshi-one">
              {groupData?.group_name}
            </p>

            <p className="text-[#757575] text-base md:text-md aoboshi-one">
              Created by {groupData?.members?.find(m => m.UserType === "Admin")?.name}
              {groupData?.members?.some(m => m.UserType === "Admin") && (
                <span className="ml-2 text-xs font-medium text-[#7C4DFF]">(Admin)</span>
              )}
            </p>

            <p className="text-[#757575] text-base md:text-md aoboshi-one break-all">
              {new Date(groupData?.created_at).toLocaleDateString()}{" "}
              {new Date(groupData?.created_at).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          </div>

          {isAdmin && (
            <button
              type="button"
              onClick={() => setShowEditModal(true)}
              className="flex items-center gap-2 flex-shrink-0 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 md:border-0 md:bg-transparent md:p-2 md:shadow-none md:hover:bg-gray-100"
              title="Edit group"
            >
              <svg className="h-5 w-5 text-gray-600 md:h-6 md:w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <span className="md:hidden">Edit Group</span>
            </button>
          )}
        </div>

        <div className="relative flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 justify-between mt-3">
            <h2 className="text-[20px] font-semibold text-black">Members</h2>
            <div className="flex gap-4 items-center">

              {isAdmin && (
                <button type="button" onClick={() => setShowAddModal(true)}>
                  <img src="./groupscreen/plus.svg" alt="Add" />
                </button>
              )}              {/* Animated inline search: toggles from the search icon */}
              {!showSearchInput ? (
                <button
                  className="mr-7"
                  type="button"
                  onClick={() => setShowSearchInput(true)}
                  aria-label="Open member search"
                >
                  <img src="../groupscreen/search.svg" alt="Search" />
                </button>
              ) : (
                <div className="mr-10 flex items-center">
                  <div
                    className={`
        flex items-center bg-white border border-gray-200 rounded-full overflow-hidden
        transition-all duration-300 ease-in-out shadow-md
        ${showSearchInput ? "w-56 px-3 py-1 opacity-100" : "w-0 opacity-0"}
        transition-[width,opacity]
      `}
                  >
                    <input
                      ref={searchRef}
                      value={memberSearch}
                      onChange={(e) => setMemberSearch(e.target.value)}
                      placeholder="Search members"
                      className="w-full outline-none text-base text-gray-800 bg-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setMemberSearch("");
                        setShowSearchInput(false);
                      }}
                      className="ml-2 p-1 rounded-full hover:bg-gray-100"
                    >
                      <img src="./chatbox/close.svg" alt="Close" className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

            </div>
          </div>

          <div className="mobile-scroll-y flex min-h-0 flex-1 flex-col gap-3 mt-3 pb-3">
            {filteredMembers.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                {(groupData?.members || []).length === 0 ? (
                  <>
                    <p className="font-semibold">No members yet</p>
                    <p className="text-xs">Add members using the + button.</p>
                  </>
                ) : (
                  <>
                    <p className="font-semibold">No members found</p>
                    <p className="text-xs">No members match "{memberSearch}".</p>
                  </>
                )}
              </div>
            ) : (
              filteredMembers.map((m) => (
                <button
                  key={m.user_id}
                  className="flex items-center w-[97%] mx-2 gap-3 p-3  bg-[#98A2B3] rounded-[100px] border border-gray-100 shadow-xl bg-opacity-[11%]"
                >
                  <img
                    src={`${process.env.REACT_APP_API_BASE_URL}${m.profile_image_url}` || "./groupscreen/dummy-user.svg"}
                    alt={m.name}
                    className="h-12 w-12 rounded-full object-cover"
                  />

                  <div className="flex justify-between items-center w-full">
                    <div className="min-w-0 flex-1 text-left">
                      <p className="flex items-center gap-2">
                        <span>{m.name}</span>
                        {m.UserType === "Admin" && (
                          <span className="text-xs font-medium text-[#7C4DFF]">(Admin)</span>
                        )}
                      </p>
                      <p className="truncate text-[#757575]">{m.phone_number}</p>
                    </div>
                    {isAdmin && m.UserType !== "Admin" && (
                      <img
                        src="./groupscreen/minus.svg"
                        alt="remove"
                        className="w-5 h-5 mr-2 cursor-pointer"
                        onClick={() => {
                          setRemoveMember(m);
                          setShowRemoveModal(true);
                        }}

                      />)}
                  </div>
                </button>
              ))
            )}
          </div>
          {isAdmin ? (
            <button
              type="button"
              className="group mt-3 mb-4 flex w-max shrink-0 items-center gap-3 rounded-full border border-[#F4C7C3] bg-gradient-to-r from-[#FFF7F6] to-[#FFEDEA] px-4 py-1 text-left text-[#C62828] shadow-[0_10px_28px_rgba(198,40,40,0.10)] transition hover:-translate-y-0.5 hover:border-[#E89A95] hover:shadow-[0_14px_34px_rgba(198,40,40,0.16)]"
              onClick={handleDeleteClick}
            >
              <div className="grid h-10 w-10 place-items-center rounded-full bg-white/90 shadow-sm ring-1 ring-[#F7D4D0] transition group-hover:scale-105">
                <img src="./groupscreen/exit.svg" alt="delete group" className="h-5 w-5" />
              </div>
              <div className="flex flex-col items-start">
                <p className="text-[15px] font-semibold leading-none">Delete Group</p>
              </div>
            </button>
          ) : (
            <button
              type="button"
              className="group mt-3 mb-4 flex w-max shrink-0 items-center gap-3 rounded-full border border-[#F4C7C3] bg-gradient-to-r from-[#FFF7F6] to-[#FFEDEA] px-4 py-2 text-left text-[#C62828] shadow-[0_10px_28px_rgba(198,40,40,0.10)] transition hover:-translate-y-0.5 hover:border-[#E89A95] hover:shadow-[0_14px_34px_rgba(198,40,40,0.16)]"
              onClick={handleExitClick}
            >
              <div className="grid h-8 w-8 place-items-center rounded-full bg-white/90 shadow-sm ring-1 ring-[#F7D4D0] transition group-hover:scale-105">
                <img src="./groupscreen/exit.svg" alt="exit group" className="h-4 w-4" />
              </div>
              <div className="flex flex-col items-start">
                <p className="text-[15px] font-semibold leading-none">Exit Group</p>
                {/* <p className="mt-1 text-xs text-[#B71C1C]/80">Leave this group safely</p> */}
              </div>
            </button>
          )}
          <RemoveModal
            open={showRemoveModal}
            onClose={() => setShowRemoveModal(false)}
            onDelete={handleRemoveMember}
            member={removeMember}
          />

          <ExitModal
            open={showExitModal}
            onClose={() => setShowExitModal(false)}
            onDelete={handleExitGroup}
          />

          <DeleteModal
            open={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onDelete={handleDeleteGroup}
          />


          {showAddModal && (
            <AddMemberModal
              fetchGroups={fetchGroups}
              groupId={groupData.group_id}
              groupMembers={groupData?.members || []}
              refreshGroupDetails={refreshGroupDetails}
              onClose={() => setShowAddModal(false)}
            />
          )}

          <EditGroupModal
            open={showEditModal}
            onClose={() => setShowEditModal(false)}
            groupData={groupData}
            refreshGroupDetails={refreshGroupDetails}
          />
        </div>
      </div>
    </>
  );
};
